import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notifications } from '@mantine/notifications';
import type { FiCountry } from '../contexts/LocaleContext';
import { validateCart } from '../lib/cart';
import { logInternalError } from '../lib/errors';
import { getProductsBySku } from '../lib/product';
import { storeShopActions } from '../reducers/storeShop';
import type { AppState } from '../types';
import i18n from '../../i18n';

// keys are the ISO-2 country codes that FiCountry is built from
const COUNTRY_NAME_KEYS: Record<FiCountry, string> = {
  US: 'united_states',
  CA: 'canada',
  GB: 'united_kingdom',
  FR: 'france',
  DE: 'germany',
  ES: 'spain',
  FI: 'finland',
  MT: 'malta',
  RO: 'romania',
  AT: 'austria',
  SI: 'slovenia',
  LV: 'latvia',
  SK: 'slovakia',
  BE: 'belgium',
  LU: 'luxembourg',
  LI: 'liechtenstein',
  NL: 'netherlands',
  SE: 'sweden',
  DK: 'denmark',
  NO: 'norway',
  CH: 'switzerland',
  IS: 'iceland',
  TR: 'turkey',
};

/**
 * Removes cart items the selected country cannot buy, once per country value. Covers every way
 * the country changes - selector, /<country>/<locale> url, session locale on login - plus mount,
 * since marketingsite writes the same country cookie. `lng` is passed in because i18n lags the
 * locale context by a tick on a country switch.
 */
// `enabled` is false while a prerendered main page still shows its locale's default market, before
// the shopper's own country is restored.
export default function useValidateCartForCountry(country: FiCountry, lng: string, enabled = true) {
  const cart = useSelector((state: AppState) => state.storeShop.cart);
  const products = useSelector((state: AppState) => state.config.products);
  const dispatch = useDispatch();

  // refs keep cart and products out of the effect deps, which the removal itself would retrigger
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const productsRef = useRef(products);
  productsRef.current = products;
  const validatedCountry = useRef<FiCountry | null>(null);

  useEffect(() => {
    if (!enabled || validatedCountry.current === country) return;
    validatedCountry.current = country;

    const currentCart = cartRef.current;
    if (Object.keys(currentCart.cartItems).length === 0) return;

    validateCart(currentCart, country)
      .then((invalidIds) => {
        if (invalidIds.length === 0) return;

        dispatch(storeShopActions.removeCartItems({ ids: invalidIds }));

        const productMap = getProductsBySku(productsRef.current);
        const invalidProductNames = invalidIds
          .map((id) => {
            const cartItem = currentCart.cartItems[id];
            if (!cartItem) return null;
            return productMap.get(cartItem.lineItem.sku)?.name ?? cartItem.lineItem.sku;
          })
          .filter(Boolean)
          .join(', ');

        notifications.show({
          message: i18n.t('cart_items_not_available_in_country', {
            lng,
            productNames: invalidProductNames || i18n.t('cart', { lng }),
            country: i18n.t(COUNTRY_NAME_KEYS[country], { lng }),
            count: invalidIds.length,
          }),
          color: 'red',
        });
      })
      .catch((err) => {
        // leave the cart alone on an unexpected failure; checkout validates the region again
        logInternalError(err, { tags: { source: 'useValidateCartForCountry' } });
      });
  }, [enabled, country, lng, dispatch]);
}
